import React, { useState } from 'react';
import {
  GraduationCap, ChevronRight, ChevronDown, ChevronUp, Calendar, Star, Info,
  Award, FileText, Target, School, Briefcase, Trophy, DollarSign, Zap,
  Search, Filter, Building2, TrendingUp
} from 'lucide-react';
import {
  schoolRankings2024,
  allSchools as staticSchoolsList,
  publicSchools,
  independentSchools
} from '../data/education-data';
import { SchoolsSnapshotCompact } from './SchoolsSnapshot';
import { LEVEL_COUNTS } from '../lib/schoolDirectory';
import { BackButton, ExpandableCard, FeatureList, FAQAccordion, ShowMoreList, DirectoryLink } from './shared';

const PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// ==================== EXAMS INFO SECTION ====================
// ==================== KEY EXAMINATIONS ====================
// Condensed from ~1.7 phone screens. The long version restated four full
// qualification descriptions and a "what students need to know" checklist that
// the dedicated /exams hub and the /exams-csec and /exams-cape guides already
// cover in more depth — three places maintaining the same facts, with this the
// one nobody would think to update.
//
// What survives is the orientation a reader needs at this point in the page
// (which exam happens when) plus routes to the real guides. It also drops four
// more accent colours (blue / purple / amber / rose chips).
const EXAM_ROUTES = [
  { page: 'exams-csec', name: 'CSEC', when: 'Grades 10-11', note: 'Equivalent to O-Levels. Needed for most jobs and further study.' },
  { page: 'exams-cape', name: 'CAPE', when: 'Grades 12-13', note: 'Equivalent to A-Levels. Needed for university admission.' },
  { page: 'exams', name: 'NVQ-J & City & Guilds', when: 'Technical & vocational', note: 'Practical skills certification in trades.' }
];

const ExamsInfoSection = ({ setPage }) => (
  <section className="mb-8">
    <div className="mb-5 flex items-start gap-4">
      <span className="hidden sm:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-brand-50 ring-1 ring-brand-100" aria-hidden="true">
        <FileText className="w-5 h-5 text-brand-700" />
      </span>
      <div>
        <h2 className="text-xl md:text-2xl font-extrabold text-ink">Key Examinations</h2>
        <p className="text-[15px] text-ink-muted mt-1">
          Jamaica follows the CXC system — most students sit CSEC in Grade 11, then CAPE in Grades 12-13.
        </p>
      </div>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {EXAM_ROUTES.map((exam) => (
        <button
          key={exam.page}
          onClick={() => setPage(exam.page)}
          className="group flex flex-col p-4 min-h-[44px] rounded-2xl text-left bg-canvas-raised ring-1 ring-hairline shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:ring-brand-200 active:scale-[0.99]"
        >
          <span className="flex items-center gap-2">
            <span className="font-bold text-[15px] text-ink">{exam.name}</span>
            <ChevronRight className="w-4 h-4 ml-auto text-ink-subtle transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
          </span>
          <span className="mt-1 inline-flex self-start text-[13px] font-semibold px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 ring-1 ring-brand-100">
            {exam.when}
          </span>
          <span className="block text-sm text-ink-muted mt-2 leading-snug">{exam.note}</span>
        </button>
      ))}
    </div>

    <p className="text-sm text-ink-muted mt-3">
      Five CSEC subjects including Maths and English is the usual bar.{' '}
      <button onClick={() => setPage('exams')} className="font-semibold text-brand-700 hover:underline underline-offset-4">
        See the full exams guide
      </button>
    </p>
  </section>
);

// ==================== CHOOSING A SCHOOL SECTION ====================
const ChoosingSchoolSection = ({ setPage }) => {
  const [expanded, setExpanded] = useState(false);

  const factors = [
    { title: 'Academic Performance', desc: 'Check CSEC/CAPE pass rates. Our rankings show school performance data.' },
    { title: 'Location & Transport', desc: 'Long commutes affect performance. Consider schools closer to home or with good bus routes.' },
    { title: 'School Type', desc: 'Traditional high schools vs technical schools - match the school to your child\'s interests.' },
    { title: 'Subject Offerings', desc: 'Not all schools offer all subjects. Check if they have what your child wants to study.' },
    { title: 'Extra-Curricular', desc: 'Sports, clubs, music - these build character and look good on university applications.' },
    { title: 'School Culture', desc: 'Visit on an open day. Talk to current students and parents if possible.' },
    { title: 'Facilities', desc: 'Science labs, computer rooms, library - these affect learning quality.' },
    { title: 'Support Services', desc: 'Guidance counselors, remedial support - important if your child needs extra help.' }
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
            <Target className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Choosing a High School</h2>
            <p className="text-sm text-gray-500">What to consider when selecting a school</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-gray-600" /> : <ChevronDown className="w-5 h-5 text-gray-600" />}
      </button>

      {expanded && (
        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {factors.map((factor, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-1">{factor.title}</h3>
                <p className="text-sm text-gray-600">{factor.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-blue-800">About PEP Placement</h4>
                <p className="text-sm text-blue-700 mt-1">
                  High school placement is determined by PEP results. Students list 5 school choices.
                  Placement depends on scores and availability. Traditional schools are more competitive.
                  Technical schools offer excellent practical training and often have more spaces.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 flex gap-3 flex-wrap">
            <button
              onClick={() => setPage('rankings')}
              className="bg-amber-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
            >
              <Trophy className="w-4 h-4" /> View Rankings
            </button>
            <button
              onClick={() => setPage('compare-schools')}
              className="bg-gray-100 text-gray-700 px-6 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center gap-2"
            >
              <Building2 className="w-4 h-4" /> Compare Schools
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const SECONDARY_FAQS = [
  {
    q: "What grades are considered high school in Jamaica?",
    a: "High school covers Grades 7-13. Grades 7-9 are lower secondary, Grades 10-11 prepare for CSEC, and Grades 12-13 (Sixth Form) are for CAPE students."
  },
  {
    q: "What's the difference between Secondary High and Technical High?",
    a: "Secondary High schools focus on academic subjects leading to CSEC/CAPE. Technical High schools offer both academic subjects and practical vocational training (NVQ-J, City & Guilds) in areas like automotive, construction, hospitality."
  },
  {
    q: "How are students placed in high school?",
    a: "Placement is based on PEP (Primary Exit Profile) results from Grade 6. Students list 5 school preferences. The Ministry places students based on scores and availability."
  },
  {
    q: "Can my child transfer to a different high school?",
    a: "Yes, transfers are possible but require approval. You'll need transfer letters from both schools, report cards, and sometimes an interview. Transfers are easier at the start of a school year."
  },
  {
    q: "What if my child fails CSEC subjects?",
    a: "Students can resit failed subjects in the January sitting or the following May/June. Many schools allow Grade 12 students to resit while doing CAPE. Private candidates can also register through CXC."
  },
  {
    q: "Is Sixth Form (CAPE) necessary?",
    a: "CAPE is required for direct university entry. However, some universities accept mature students or those with CSEC plus work experience. CAPE gives better scholarship opportunities and higher starting positions."
  },
  {
    q: "What support is available for struggling students?",
    a: "Most schools have guidance counselors and remedial programmes. The Ministry offers literacy and numeracy interventions. Some schools have peer tutoring. PATH benefits continue through high school for eligible families."
  }
];

// ==================== CAREER PLANNING TOOLS ====================
const QUICK_RESOURCES = [
  { title: 'Rankings', icon: Award, page: 'rankings' },
  { title: 'CSEC Guide', icon: FileText, page: 'exams-csec' },
  { title: 'CAPE Guide', icon: GraduationCap, page: 'exams-cape' },
  { title: 'Compare', icon: Building2, page: 'compare-schools' }
];

// ==================== TOOLS & RESOURCES ====================
// Replaces four separately-headed sections — "Career Planning Tools",
// "Student Success Tools", "Earn & Learn Opportunities" and the "High School
// Resources" link grid. Together they ran ~1.9 phone screens to present what
// is, in substance, one list: seven tools and four shortcuts. Four headings for
// one idea also meant four scroll stops before the reader worked out the
// sections were interchangeable.
//
// The cards were also the last holdout of the old palette — each tool picked
// its own gradient (purple/violet, blue/indigo, cyan, orange/amber,
// purple/pink), so this strip alone carried six accent colours. They are now
// uniform white cards with a brand-tinted icon, which is both calmer and more
// legible: card copy goes from white-on-mid-tone to near-black on white.
const HUB_TOOLS = [
  { page: 'career-path', icon: Target, title: 'Career Path Visualiser', desc: 'Explore careers by your interests, values and skills' },
  { page: 'life-path', icon: TrendingUp, title: 'Life Path Simulator', desc: 'Compare education routes and what they cost' },
  { page: 'exam-tracker', icon: Calendar, title: 'Exam Tracker', desc: 'Track exam dates and study resources' },
  { page: 'extracurriculars', icon: Star, title: 'Extracurricular Activities', desc: 'Clubs, sports and programmes' },
  { page: 'side-hustles', icon: DollarSign, title: 'Student Side Hustles', desc: 'Safe, legal ways to earn while you learn' },
  { page: 'summer-employment', icon: Briefcase, title: 'Summer Employment', desc: 'Summer jobs, internships and programmes' },
  { page: 'opportunity-matcher', icon: Zap, title: 'Opportunity Matcher', desc: 'Match your interests with jobs and internships' }
];

const ToolsAndResources = ({ setPage }) => (
  <section className="mb-8">
    <div className="mb-5 flex items-start gap-4">
      <span className="hidden sm:flex flex-shrink-0 items-center justify-center w-11 h-11 rounded-xl bg-brand-50 ring-1 ring-brand-100" aria-hidden="true">
        <Zap className="w-5 h-5 text-brand-700" />
      </span>
      <div>
        <h2 className="text-xl md:text-2xl font-extrabold text-ink">Tools &amp; Resources</h2>
        <p className="text-[15px] text-ink-muted mt-1">Plan a career, track exams, and find ways to earn while you study.</p>
      </div>
    </div>

    <ShowMoreList items={HUB_TOOLS} initial={4} noun="tools" className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {(tool) => (
        <button
          key={tool.page}
          onClick={() => setPage(tool.page)}
          className="group flex items-start gap-3.5 p-4 min-h-[44px] rounded-2xl text-left bg-canvas-raised ring-1 ring-hairline shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 hover:ring-brand-200 active:scale-[0.99]"
        >
          <span className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-brand-50 ring-1 ring-brand-100 transition-transform duration-200 group-hover:scale-110">
            <tool.icon className="w-5 h-5 text-brand-700" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <span className="block font-bold text-[15px] text-ink leading-tight">{tool.title}</span>
            <span className="block text-sm text-ink-muted mt-1 leading-snug">{tool.desc}</span>
          </span>
          <ChevronRight className="w-4 h-4 ml-auto mt-3 flex-shrink-0 text-ink-subtle transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
        </button>
      )}
    </ShowMoreList>

    {/* The former "High School Resources" grid — shortcuts to pages that already
        exist elsewhere in the nav, so they read as a footnote to this section
        rather than earning a heading of their own. */}
    <div className="mt-4 pt-4 border-t border-hairline flex flex-wrap gap-2">
      {QUICK_RESOURCES.map((r) => (
        <button
          key={r.page}
          onClick={() => setPage(r.page)}
          className="inline-flex items-center gap-2 px-4 min-h-[44px] rounded-xl text-sm font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-100 transition-all duration-150 hover:bg-brand-100 active:scale-[0.97]"
        >
          <r.icon className="w-4 h-4" aria-hidden="true" />
          {r.title}
        </button>
      ))}
    </div>
  </section>
);

// ==================== SCHOOL DIRECTORY SECTION ====================
export const SchoolDirectory = ({ setPage }) => {
  const [filters, setFilters] = useState({
    parish: '',
    type: '',
    sector: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const allSchoolsWithRankings = staticSchoolsList.map(school => {
    const ranking = schoolRankings2024.find(r =>
      r.school_name.toLowerCase() === school.school_name.toLowerCase()
    );
    return {
      ...school,
      id: school.school_name,
      name: school.school_name,
      rank: ranking?.rank || null,
      score: ranking?.score || null
    };
  });

  const filteredSchools = allSchoolsWithRankings.filter(school => {
    if (filters.parish && school.parish !== filters.parish) return false;
    if (filters.type && school.school_type !== filters.type) return false;
    if (filters.sector && school.sector !== filters.sector) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      if (!school.name.toLowerCase().includes(searchLower) &&
          !school.parish.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    return true;
  });

  const schoolsList = [...filteredSchools].sort((a, b) => {
    if (a.rank && b.rank) return a.rank - b.rank;
    if (a.rank) return -1;
    if (b.rank) return 1;
    return a.name.localeCompare(b.name);
  });

  // Restyle: initial page trimmed 20 -> 10. At 20 rows the directory alone
  // ran past three phone screens before the rest of the hub page even
  // started; the full list is still one tap away.
  const INITIAL_SCHOOLS = 10;
  const displayedSchools = showAll ? schoolsList : schoolsList.slice(0, INITIAL_SCHOOLS);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center">
            <School className="w-6 h-6 text-brand-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">School Directory</h2>
            <p className="text-sm text-gray-500">{staticSchoolsList.length} high schools across Jamaica</p>
          </div>
        </div>
      </div>

      {/* Stats */}
      {/* Four counts of the same kind of thing do not need four different
          colours — the tint was carrying no meaning here, just leftover palette
          (brand / blue / purple / amber). */}
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { value: staticSchoolsList.length, label: 'Total Schools' },
          { value: publicSchools.length, label: 'Public' },
          { value: independentSchools.length, label: 'Independent' },
          { value: schoolRankings2024.length, label: 'Ranked' }
        ].map((stat) => (
          <div key={stat.label} className="bg-canvas-sunken ring-1 ring-hairline rounded-xl p-3 text-center">
            <dd className="text-2xl font-extrabold text-brand-700 tabular-nums">{stat.value}</dd>
            <dt className="text-xs text-ink-muted mt-0.5">{stat.label}</dt>
          </div>
        ))}
      </dl>

      {/* Search & Filters */}
      <div className="bg-gray-50 rounded-xl p-4 mb-4">
        <div className="flex gap-3 mb-3">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search schools..."
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-5 h-5" />
            Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-gray-200">
            <select
              value={filters.parish}
              onChange={e => setFilters(f => ({ ...f, parish: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="">All Parishes</option>
              {PARISHES.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
            <select
              value={filters.type}
              onChange={e => setFilters(f => ({ ...f, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="">All Types</option>
              <option value="Secondary High">Secondary High</option>
              <option value="Technical High">Technical High</option>
            </select>
            <select
              value={filters.sector}
              onChange={e => setFilters(f => ({ ...f, sector: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-lg bg-white"
            >
              <option value="">All Sectors</option>
              <option value="Public">Public</option>
              <option value="Independent">Independent</option>
            </select>
            <button
              onClick={() => setFilters({ parish: '', type: '', sector: '', search: '' })}
              className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Results */}
      {schoolsList.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <School className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-600 mb-2">No Schools Found</h3>
          <p className="text-gray-500">Try adjusting your filters or search terms.</p>
        </div>
      ) : (
        <>
          <p className="text-sm text-gray-500 mb-3">{schoolsList.length} schools found</p>
          <div className="space-y-2">
            {displayedSchools.map((school, index) => (
              <div
                key={school.id}
                className="bg-gray-50 rounded-lg p-3 flex items-center gap-3 hover:bg-gray-100 transition-colors"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-xs ${
                  school.rank ? (school.rank <= 10 ? 'bg-amber-100 text-amber-700' : 'bg-brand-100 text-brand-700') : 'bg-gray-200 text-gray-500'
                }`}>
                  {school.rank ? `#${school.rank}` : index + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-800 text-sm">{school.name}</h3>
                  <div className="flex flex-wrap gap-2 mt-0.5">
                    <span className="text-xs text-gray-500">{school.parish}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      school.sector === 'Public' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {school.sector}
                    </span>
                  </div>
                </div>
                {school.score && (
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-bold text-brand-600">{school.score}</div>
                    <div className="text-xs text-gray-400">Score</div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {schoolsList.length > INITIAL_SCHOOLS && (
            <button
              onClick={() => setShowAll(!showAll)}
              aria-expanded={showAll}
              className="w-full sm:w-auto mt-4 inline-flex items-center justify-center gap-2 min-h-[44px] px-5 py-3 rounded-xl text-[15px] font-semibold text-brand-700 bg-brand-50 ring-1 ring-brand-100 transition-all duration-150 hover:bg-brand-100 hover:ring-brand-200 active:scale-[0.97]"
            >
              {showAll
                ? 'Show fewer schools'
                : `Show all ${schoolsList.length} schools`}
              {showAll ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          )}
        </>
      )}

      <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs text-gray-500 text-center">
        Data: Ministry of Education Directory & Educate Jamaica Performance Index 2024
      </div>
    </div>
  );
};

// ==================== MAIN SECONDARY EDUCATION HUB ====================
export const SecondaryEducationHub = ({ setPage, goBack }) => {
  const schoolTypes = [
    {
      icon: School,
      title: "Traditional High School",
      description: "Academic-focused schools preparing students for CSEC and CAPE examinations. Emphasis on core subjects.",
      features: [
        "Strong focus on academic subjects (Sciences, Languages, Mathematics)",
        "Preparation for CSEC (Grade 11) and CAPE (Grade 12-13)",
        "Wide range of subject choices",
        "Usually more competitive for entry",
        "Many have strong sports and arts programmes"
      ]
    },
    {
      icon: Briefcase,
      title: "Technical High School",
      description: "Combines academics with practical vocational training. Students graduate with both CSEC and technical certifications.",
      features: [
        "Offers NVQ-J and City & Guilds certifications",
        "Practical skills: automotive, electrical, construction, hospitality",
        "Can enter workforce immediately or continue to tertiary",
        "Often better employment outcomes for certain careers",
        "Growing demand for technical graduates"
      ]
    },
    {
      icon: GraduationCap,
      title: "Sixth Form / CAPE",
      description: "Advanced studies for students who completed CSEC. Required for direct university entry.",
      features: [
        "Grades 12-13 (two-year programme)",
        "Choose 2-5 CAPE subjects based on career goals",
        "University preparation and applications support",
        "Leadership opportunities (prefects, clubs)",
        "Some schools offer Associate Degrees alongside"
      ]
    }
  ];

  return (
    <div className="w-full">
      <BackButton goBack={goBack} />

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-door-secondary to-door-secondary-dark rounded-xl sm:rounded-2xl p-4 sm:p-8 text-white mb-6 sm:mb-8">
        <div className="flex items-center gap-2 text-white/80 text-sm mb-2">
          <School className="w-4 h-4 sm:w-5 sm:h-5" />
          <span>Secondary Education Guide</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-black mb-2">High School in Jamaica</h1>
        <p className="text-white/90 text-base sm:text-lg">
          Everything you need to know about secondary education, from Grade 7 to Sixth Form
        </p>

        {/* Key Stats */}
        <div className="grid grid-cols-3 gap-2 sm:gap-4 mt-4 sm:mt-6">
          <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
            <div className="text-xl sm:text-2xl font-black">{staticSchoolsList.length}+</div>
            <div className="text-xs text-white/70">High Schools</div>
          </div>
          <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
            <div className="text-xl sm:text-2xl font-black">7</div>
            <div className="text-xs text-white/70">Years (Gr. 7-13)</div>
          </div>
          <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 text-center">
            <div className="text-xl sm:text-2xl font-black">14</div>
            <div className="text-xs text-white/70">Parishes</div>
          </div>
        </div>
      </div>

      {/* School Directory — FIRST.
          This is the page's job. It used to sit dead last, 8.1 phone screens
          down, behind eight sections of guidance: someone tapping "High
          Schools" to look up a school had to scroll past all of it. Everything
          below is supporting material and now reads as such. */}
      {/* Two ways in, and they answer different questions. This one opens the
          whole-country directory with Secondary preselected — the Ministry's
          full secondary list, searchable by parish and school type. The ranked
          listing below stays here because rank and CSEC score only exist for
          secondary schools: it is specific to this page in a way it never was
          to /all-schools.

          The label names the filter. An earlier version read "Search every
          school in Jamaica" while still passing level=Secondary, so the
          destination opened showing 253 of 1,654 — the button described one
          thing and did another. */}
      <DirectoryLink
        setPage={setPage}
        level="Secondary"
        label="Search all secondary schools"
        count={LEVEL_COUNTS.Secondary}
        className="mb-8"
      />

      <SchoolDirectory setPage={setPage} />

      {/* Rankings teaser.
          Was a 2.1-screen embed of the full SchoolsSnapshot in the page's prime
          slot, duplicating /rankings. SchoolsSnapshotCompact already existed in
          SchoolsSnapshot.jsx and was never imported anywhere — it is a 3-row
          top-schools teaser with its own heading and "See all" link, which is
          all this slot needs. */}
      <div className="mb-8">
        <SchoolsSnapshotCompact setPage={setPage} />
      </div>

      {/* School Types */}
      <div className="mb-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Types of High Schools</h2>
        <ShowMoreList items={schoolTypes} initial={3} noun="school types" className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(type, idx) => (
            <ExpandableCard key={idx} accent="secondary" {...type}>
              <FeatureList items={type.features} />
            </ExpandableCard>
          )}
        </ShowMoreList>
      </div>

      {/* Exams Section */}
      <ExamsInfoSection setPage={setPage} />

      {/* Choosing a School */}
      <ChoosingSchoolSection setPage={setPage} />

      {/* Tools & Resources — the former Career Planning / Student Success /
          Earn & Learn / High School Resources sections, merged. */}
      <ToolsAndResources setPage={setPage} />


      {/* FAQ */}
      <FAQAccordion faqs={SECONDARY_FAQS} />
    </div>
  );
};

export default SecondaryEducationHub;
